import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { bankApi, accountApi } from '@/services/api'
import type { BankLine, BankAccount, Account, ReconciliationSummary, SmartMatchSuggestion } from '@/types'
import { formatCurrency } from '@/utils/currency'
import {
  Check, Ban, X, ArrowRightLeft, Zap, Link2, CheckCircle2,
  ChevronRight, AlertCircle, ArrowDownLeft, ArrowUpRight,
  Paperclip, Trash2, Upload,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

type ActionTab = 'match' | 'create'

function statusColor(status: string) {
  if (status === 'reconciled') return 'text-emerald-600'
  if (status === 'matched') return 'text-[#0078C8]'
  if (status === 'excluded') return 'text-[#CCCED2]'
  return 'text-amber-600'
}

function statusBg(status: string) {
  if (status === 'reconciled') return 'bg-emerald-50 text-emerald-700'
  if (status === 'matched') return 'bg-blue-50 text-[#0078C8]'
  if (status === 'excluded') return 'bg-gray-50 text-[#CCCED2]'
  return 'bg-amber-50 text-amber-700'
}

export default function Reconciliation() {
  const { clientId, bankAccountId } = useParams<{ clientId: string; bankAccountId: string }>()

  const [lines, setLines] = useState<BankLine[]>([])
  const [bankCurrency, setBankCurrency] = useState('USD')
  const [accounts, setAccounts] = useState<Account[]>([])
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('unreconciled')

  const [selectedLine, setSelectedLine] = useState<BankLine | null>(null)
  const [actionTab, setActionTab] = useState<ActionTab>('match')
  const [suggestions, setSuggestions] = useState<SmartMatchSuggestion[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [attaching, setAttaching] = useState(false)
  const attachInputRef = useRef<HTMLInputElement>(null)

  const loadData = useCallback(async () => {
    if (!bankAccountId || !clientId) return
    setLoading(true)
    try {
      const [linesRes, accRes, summaryRes, bankAccountsRes] = await Promise.all([
        bankApi.listLines(bankAccountId, filter !== 'all' ? filter : undefined),
        accountApi.list(clientId),
        bankApi.reconciliationSummary(bankAccountId),
        bankApi.list(clientId),
      ])
      setLines(linesRes.data.bank_lines)
      setAccounts(accRes.data.accounts)
      setSummary(summaryRes.data)
      const acct = (bankAccountsRes.data.bank_accounts as BankAccount[]).find((a) => a.id === bankAccountId)
      if (acct) setBankCurrency(acct.currency)
    } finally {
      setLoading(false)
    }
  }, [bankAccountId, clientId, filter])

  useEffect(() => { loadData() }, [loadData])

  // Reload lines when filter changes (without full reload)
  useEffect(() => {
    if (!bankAccountId || loading) return
    bankApi.listLines(bankAccountId, filter !== 'all' ? filter : undefined).then((res) => {
      setLines(res.data.bank_lines)
    })
  }, [filter]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLineClick(line: BankLine) {
    setSelectedLine(line)
    setSelectedAccount('')
    setDescription(line.description || '')
    setSuggestions([])

    if (line.status === 'unreconciled') {
      setActionTab('match')
      setSuggestionsLoading(true)
      try {
        const res = await bankApi.getSuggestions(line.id)
        setSuggestions(res.data.suggestions)
        // If no suggestions, switch to create tab
        if (res.data.suggestions.length === 0) setActionTab('create')
      } finally {
        setSuggestionsLoading(false)
      }
    }
  }

  async function handleMatchExisting(suggestion: SmartMatchSuggestion) {
    if (!selectedLine) return
    setSubmitting(true)
    try {
      await bankApi.matchTransaction(selectedLine.id, suggestion.transaction_id)
      toast.success('Matched — click Approve to confirm')
      setSelectedLine(null)
      await loadData()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleApprove(line: BankLine) {
    setSubmitting(true)
    try {
      await bankApi.approveLine(line.id)
      toast.success('Reconciled')
      setSelectedLine(null)
      await loadData()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReconcile() {
    if (!selectedLine || !selectedAccount) return
    setSubmitting(true)
    try {
      await bankApi.reconcileLine(selectedLine.id, {
        account_id: selectedAccount,
        description: description || selectedLine.description,
      })
      toast.success('Reconciled')
      setSelectedLine(null)
      await loadData()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleExclude(line: BankLine) {
    try {
      await bankApi.excludeLine(line.id)
      toast.success('Excluded')
      setSelectedLine(null)
      await loadData()
    } catch {
      toast.error('Failed to exclude')
    }
  }

  async function handleBulkApprove() {
    if (!bankAccountId) return
    try {
      const res = await bankApi.bulkApprove(bankAccountId)
      setSummary(res.data)
      toast.success('All matched lines approved')
      setSelectedLine(null)
      bankApi.listLines(bankAccountId, filter !== 'all' ? filter : undefined).then((r) => {
        setLines(r.data.bank_lines)
      })
    } catch {
      toast.error('Failed to bulk approve')
    }
  }

  async function handleAttach(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedLine) return
    setAttaching(true)
    try {
      const res = await bankApi.attachFile(selectedLine.id, file)
      const updated: BankLine = res.data
      setSelectedLine(updated)
      setLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)))
      toast.success('Receipt attached')
    } catch {
      toast.error('Failed to attach file')
    } finally {
      setAttaching(false)
      if (attachInputRef.current) attachInputRef.current.value = ''
    }
  }

  async function handleDetach() {
    if (!selectedLine) return
    setAttaching(true)
    try {
      const res = await bankApi.detachFile(selectedLine.id)
      const updated: BankLine = res.data
      setSelectedLine(updated)
      setLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)))
      toast.success('Attachment removed')
    } catch {
      toast.error('Failed to remove attachment')
    } finally {
      setAttaching(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const isPositive = (amount: string) => parseFloat(amount) >= 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Reconciliation</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Match bank transactions to your accounts</p>
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Total', value: summary.total_lines, cls: 'text-[#000A1E]' },
            { label: 'Unreconciled', value: summary.unreconciled, cls: 'text-amber-600', amount: summary.unreconciled_amount },
            { label: 'Matched', value: summary.matched, cls: 'text-[#0078C8]' },
            { label: 'Reconciled', value: summary.reconciled, cls: 'text-emerald-600', amount: summary.reconciled_amount },
            { label: 'Excluded', value: summary.excluded, cls: 'text-[#CCCED2]' },
          ].map((s) => (
            <div key={s.label} className="card px-4 py-3">
              <p className={`text-xl font-bold ${s.cls}`}>{s.value}</p>
              <p className="text-[11px] text-[rgba(0,10,30,0.75)] mt-0.5">{s.label}</p>
              {s.amount !== undefined && (
                <p className="text-[11px] text-[#CCCED2] tabular-nums mt-0.5">{formatCurrency(s.amount, bankCurrency)}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bulk approve banner */}
      {summary && summary.matched > 0 && (
        <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Zap className="w-4 h-4 text-[#0078C8] flex-shrink-0" />
            <p className="text-[13px] text-[#000A1E]">
              <span className="font-bold">{summary.matched} matched</span> transaction{summary.matched !== 1 ? 's' : ''} — rules applied, ready to approve
            </p>
          </div>
          <button onClick={handleBulkApprove} className="btn-primary py-1.5 text-xs">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve All
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 border-b-2 border-[#CCCED2]">
        {[
          { key: 'unreconciled', label: 'To Do', count: summary?.unreconciled },
          { key: 'matched', label: 'Matched', count: summary?.matched },
          { key: 'reconciled', label: 'Reconciled', count: summary?.reconciled },
          { key: 'excluded', label: 'Excluded', count: summary?.excluded },
          { key: 'all', label: 'All', count: summary?.total_lines },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              'flex items-center gap-1 px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] transition-colors',
              filter === f.key
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent'
            )}
          >
            {f.label}
            {f.count !== undefined && f.count > 0 && (
              <span className={cn(
                'text-[10px] px-1.5 py-0.5 rounded-full',
                filter === f.key ? 'bg-[#0078C8]/10 text-[#0078C8]' : 'bg-[#E8E9EB] text-[rgba(0,10,30,0.75)]'
              )}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Split pane */}
      <div className="flex gap-4 items-start">
        {/* Bank lines list */}
        <div className="flex-1 card overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-200 bg-surface-50 flex items-center justify-between">
            <p className="text-[13px] font-bold text-[#000A1E]">
              Statement Lines <span className="text-[rgba(0,10,30,0.75)] font-normal ml-1">({lines.length})</span>
            </p>
          </div>
          <div className="divide-y divide-surface-100 max-h-[calc(100vh-340px)] overflow-y-auto">
            {lines.map((line) => {
              const positive = isPositive(line.amount)
              const isSelected = selectedLine?.id === line.id
              return (
                <div
                  key={line.id}
                  onClick={() => handleLineClick(line)}
                  className={cn(
                    'px-4 py-3 cursor-pointer transition-colors border-l-2',
                    isSelected
                      ? 'bg-[#0078C8]/5 border-l-[#0078C8]'
                      : 'border-l-transparent hover:bg-surface-50'
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    {/* Direction icon */}
                    <div className={`mt-0.5 flex-shrink-0 ${positive ? 'text-emerald-500' : 'text-[rgba(0,10,30,0.75)]'}`}>
                      {positive
                        ? <ArrowDownLeft className="w-4 h-4" />
                        : <ArrowUpRight className="w-4 h-4" />
                      }
                    </div>
                    {/* Main info */}
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-[#000A1E] truncate font-medium">
                        {line.description || 'No description'}
                      </p>
                      {(line.status === 'matched' || line.status === 'reconciled') && line.matched_transaction_description && (
                        <p className="text-[13px] text-[#0078C8] truncate mt-0.5 flex items-center gap-1">
                          <Link2 className="w-3 h-3 flex-shrink-0" />
                          {line.matched_transaction_description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[13px] text-[rgba(0,10,30,0.75)]">{line.date}</span>
                        {line.reference && (
                          <span className="text-[13px] text-[rgba(0,10,30,0.75)]">&middot; {line.reference}</span>
                        )}
                      </div>
                    </div>
                    {/* Amount + status */}
                    <div className="text-right flex-shrink-0">
                      <p className={`text-[13px] font-bold tabular-nums ${positive ? 'text-emerald-600' : 'text-[#000A1E]'}`}>
                        {positive ? '+' : ''}{formatCurrency(line.amount, bankCurrency)}
                      </p>
                      <span className={`inline-block mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize ${statusBg(line.status)}`}>
                        {line.status}
                      </span>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-0.5">
                      {line.attached_document_id && (
                        <Paperclip className="w-3 h-3 text-[#0078C8]" />
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-[#CCCED2] mt-1" />
                    </div>
                  </div>
                </div>
              )
            })}
            {lines.length === 0 && (
              <div className="px-4 py-16 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-[13px] text-[#000A1E] font-medium">All caught up!</p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">No lines match this filter</p>
              </div>
            )}
          </div>
        </div>

        {/* Action panel */}
        <div className="w-[340px] flex-shrink-0 sticky top-4">
          <div className="card overflow-hidden">
            {!selectedLine ? (
              <div className="p-6 py-12 text-center">
                <ArrowRightLeft className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
                <p className="text-[13px] font-bold text-[#000A1E]">Select a statement line</p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">Click any line on the left to reconcile it</p>
              </div>
            ) : (
              <>
                {/* Selected line detail */}
                <div className="px-4 py-3 border-b border-surface-100 bg-surface-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-bold text-[#000A1E] truncate">{selectedLine.description || 'No description'}</p>
                      <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">{selectedLine.date}{selectedLine.reference ? ` · ${selectedLine.reference}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-base font-bold tabular-nums ${isPositive(selectedLine.amount) ? 'text-emerald-600' : 'text-[#000A1E]'}`}>
                        {isPositive(selectedLine.amount) ? '+' : ''}{formatCurrency(selectedLine.amount, bankCurrency)}
                      </p>
                      <span className={`text-[10px] ${statusColor(selectedLine.status)} font-medium capitalize`}>
                        {selectedLine.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Attachment section — shown for unreconciled + matched */}
                {(selectedLine.status === 'unreconciled' || selectedLine.status === 'matched') && (
                  <div className="px-4 py-3 border-b border-surface-100">
                    <p className="text-[11px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-wide mb-2">
                      Receipt / Attachment
                    </p>
                    {selectedLine.attached_document_id ? (
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-3.5 h-3.5 text-[#0078C8] flex-shrink-0" />
                        <a
                          href={selectedLine.attached_document_url ?? '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[13px] text-[#0078C8] font-bold hover:underline truncate flex-1"
                        >
                          {selectedLine.attached_document_name}
                        </a>
                        <button
                          onClick={handleDetach}
                          disabled={attaching}
                          className="text-red-400 hover:text-red-600 flex-shrink-0"
                          title="Remove attachment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className={cn(
                        'flex items-center gap-1.5 text-[13px] cursor-pointer text-[#0078C8] font-bold hover:underline',
                        attaching && 'opacity-50 pointer-events-none'
                      )}>
                        <Upload className="w-3.5 h-3.5" />
                        {attaching ? 'Attaching...' : 'Attach receipt'}
                        <input
                          ref={attachInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleAttach}
                          disabled={attaching}
                          accept="application/pdf,image/*,.csv"
                        />
                      </label>
                    )}
                  </div>
                )}

                <div className="p-4">
                  {/* MATCHED state — approve or unmatch */}
                  {selectedLine.status === 'matched' && (
                    <div>
                      <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                        <Link2 className="w-4 h-4 text-[#0078C8] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[13px] font-bold text-[#000A1E]">Matched to existing transaction</p>
                          {selectedLine.matched_transaction_description && (
                            <p className="text-[13px] text-[#0078C8] mt-0.5">{selectedLine.matched_transaction_description}</p>
                          )}
                          <p className="text-[11px] text-[rgba(0,10,30,0.75)] mt-1">Click Approve to confirm reconciliation</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <button
                          onClick={() => handleApprove(selectedLine)}
                          disabled={submitting}
                          className="btn-primary w-full"
                        >
                          <Check className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={() => handleExclude(selectedLine)}
                          className="btn-secondary w-full"
                        >
                          <Ban className="w-4 h-4" /> Exclude
                        </button>
                        <button onClick={() => setSelectedLine(null)} className="btn-ghost w-full justify-center text-xs">
                          <X className="w-3.5 h-3.5" /> Deselect
                        </button>
                      </div>
                    </div>
                  )}

                  {/* RECONCILED / EXCLUDED — read only */}
                  {(selectedLine.status === 'reconciled' || selectedLine.status === 'excluded') && (
                    <div>
                      <div className={cn(
                        'flex items-start gap-2 rounded-lg p-3 mb-4',
                        selectedLine.status === 'reconciled' ? 'bg-emerald-50 border border-emerald-100' : 'bg-gray-50 border border-gray-100'
                      )}>
                        {selectedLine.status === 'reconciled'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          : <AlertCircle className="w-4 h-4 text-[#CCCED2] mt-0.5 flex-shrink-0" />
                        }
                        <p className={cn(
                          'text-[13px] font-medium',
                          selectedLine.status === 'reconciled' ? 'text-emerald-800' : 'text-[rgba(0,10,30,0.75)]'
                        )}>
                          {selectedLine.status === 'reconciled' ? 'This line is reconciled.' : 'This line is excluded.'}
                        </p>
                      </div>
                      <button onClick={() => setSelectedLine(null)} className="btn-ghost w-full justify-center text-xs">
                        <X className="w-3.5 h-3.5" /> Deselect
                      </button>
                    </div>
                  )}

                  {/* UNRECONCILED — match or create */}
                  {selectedLine.status === 'unreconciled' && (
                    <div>
                      {/* Tabs */}
                      <div className="flex gap-1 mb-4 border-b-2 border-[#CCCED2]">
                        <button
                          onClick={() => setActionTab('match')}
                          className={cn(
                            'flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] transition-colors',
                            actionTab === 'match'
                              ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                              : 'text-[#0078C8] font-normal border-b-transparent'
                          )}
                        >
                          <Link2 className="w-3 h-3" /> Match Existing
                        </button>
                        <button
                          onClick={() => setActionTab('create')}
                          className={cn(
                            'flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] transition-colors',
                            actionTab === 'create'
                              ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                              : 'text-[#0078C8] font-normal border-b-transparent'
                          )}
                        >
                          <Check className="w-3 h-3" /> Create New
                        </button>
                      </div>

                      {/* Match existing */}
                      {actionTab === 'match' && (
                        <div>
                          {suggestionsLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-4 h-4 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : suggestions.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-[11px] text-[rgba(0,10,30,0.75)] font-bold uppercase tracking-wide mb-2">Suggested matches</p>
                              {suggestions.map((s) => (
                                <div
                                  key={s.transaction_id}
                                  className="border border-surface-200 rounded-lg p-3 hover:border-[#0078C8] hover:bg-[#0078C8]/5 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                      <p className="text-[13px] font-medium text-[#000A1E] truncate">{s.description || 'Transaction'}</p>
                                      <p className="text-[11px] text-[rgba(0,10,30,0.75)] mt-0.5">{s.date} {s.reference ? `· ${s.reference}` : ''}</p>
                                    </div>
                                    <p className="text-[13px] font-bold tabular-nums text-[#000A1E] flex-shrink-0">
                                      {formatCurrency(s.amount, bankCurrency)}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleMatchExisting(s)}
                                    disabled={submitting}
                                    className="w-full btn-secondary py-1 text-xs"
                                  >
                                    Match
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <ArrowRightLeft className="w-6 h-6 text-[#CCCED2] mx-auto mb-2" />
                              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">No matching transactions found</p>
                              <p className="text-[11px] text-[rgba(0,10,30,0.75)] mt-0.5">Try creating a new one below</p>
                              <button
                                onClick={() => setActionTab('create')}
                                className="mt-3 text-[13px] text-[#0078C8] font-bold hover:underline"
                              >
                                Create new transaction →
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Create new */}
                      {actionTab === 'create' && (
                        <div className="space-y-3">
                          <div>
                            <label className="input-label">Description</label>
                            <input
                              type="text"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              className="input text-[13px]"
                              placeholder={selectedLine.description || 'Transaction description'}
                            />
                          </div>
                          <div>
                            <label className="input-label">
                              {isPositive(selectedLine.amount) ? 'Received Into Account' : 'Paid From Account'}
                            </label>
                            <select
                              value={selectedAccount}
                              onChange={(e) => setSelectedAccount(e.target.value)}
                              className="input"
                            >
                              <option value="">Select account...</option>
                              {accounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.code} — {a.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-4 space-y-2">
                        {actionTab === 'create' && (
                          <button
                            onClick={handleReconcile}
                            disabled={!selectedAccount || submitting}
                            className="btn-primary w-full"
                          >
                            <Check className="w-4 h-4" />
                            {isPositive(selectedLine.amount) ? 'Receive Money' : 'Spend Money'}
                          </button>
                        )}
                        <button
                          onClick={() => handleExclude(selectedLine)}
                          className="btn-secondary w-full"
                        >
                          <Ban className="w-4 h-4" /> Exclude
                        </button>
                        <button onClick={() => setSelectedLine(null)} className="btn-ghost w-full justify-center text-xs">
                          <X className="w-3.5 h-3.5" /> Deselect
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
